import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import { Button, Icon } from "semantic-ui-react";

import useHub from "data/hub/useHub";

import useRedtailUrl from "components/Crm/hooks/useRedtailUrl";
import Form from "components/Form";
import Modal from "components/Modal";

import { useMeetingForm } from "./hooks";

function MeetingFormModal({ meeting, onClose, onDone }) {
  const hub = useHub();
  const redtailUrl = useRedtailUrl();
  const { isEdit, form, options, loaders, submit } = useMeetingForm(
    meeting,
    () => {
      onDone();
      onClose();
    }
  );

  return (
    <Modal isModalOpen size="mini" onClose={onClose}>
      <Modal.Header onClose={onClose}>
        <span>{isEdit ? "Edit Meeting" : "Add Meeting"}</span>
      </Modal.Header>
      <Modal.Body scrolling>
        <Form {...form} id="meetingForm" onSubmit={form.handleSubmit(submit)}>
          <Form.Input
            name="name"
            label="Name"
            fluid
            required
            placeholder="Enter a name"
            data-test="meeting-name-input"
          />
          <Form.Dropdown
            name="type"
            label="Type"
            description={
              !options.meetingTypes?.length && !loaders?.isLoadingOptions ? (
                <span>
                  You are not syncing any meeting types at the moment. Please
                  visit{" "}
                  <Link to={`/hub/${hub.hubId}/settings/hub?tab=meetings`}>
                    the hub settings
                  </Link>{" "}
                  to select which meeting types you would like to sync.
                </span>
              ) : null
            }
            placeholder="Select a type"
            data-test="meeting-type-dropdown"
            required
            autoHighlight
            options={options.meetingTypes}
            loading={loaders.isLoadingOptions}
            getOptionSelected={(o, v) => {
              return o.id === v.id;
            }}
            getOptionLabel={({ name }) => {
              return name || "";
            }}
            renderOption={({ name }) => {
              return <React.Fragment>{name}</React.Fragment>;
            }}
          />
          <Form.DateTimePicker
            name="startDate"
            label="Start Date"
            placeholderText="e.g. 02/04/2022 6:45pm"
            dateFormat="MM/dd/yyyy h:mmaaa"
            timeIntervals={15}
            required
            showTimeSelect
            dataTest="meeting-start-date-picker"
          />
          <Form.DateTimePicker
            name="endDate"
            label="End Date"
            placeholderText="e.g. 02/04/2022 7:45pm"
            dateFormat="MM/dd/yyyy h:mmaaa"
            timeIntervals={15}
            required
            showTimeSelect
            dataTest="meeting-end-date-picker"
          />
          <Form.Dropdown
            name="attendees"
            label="Attendees"
            placeholder="Select attendees"
            options={options.users}
            loading={options.isLoadingUsers}
            data-test="meeting-attendees-dropdown"
            required
            multiple
            disableCloseOnSelect
            disabled={Boolean(hub.clientPool.type === "redtail" && isEdit)}
            description={
              hub.clientPool.type === "redtail" && isEdit ? (
                <a
                  href={`${redtailUrl}/activities/${meeting?.externalId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline_on_hover"
                  data-test="redtail-attendees-link"
                  style={{
                    display: "flex",
                    gap: ".5em",
                    alignItems: "baseline",
                  }}
                >
                  Attendees can only be changed in Redtail{" "}
                  <Icon name="external alternate" />
                </a>
              ) : null
            }
            getOptionSelected={(o, v) => {
              return o.id === v.id;
            }}
            getOptionLabel={({ name }) => {
              return name || "";
            }}
            renderOption={({ name }) => {
              return <React.Fragment>{name}</React.Fragment>;
            }}
            getTagValue={({ name }) => {
              return name;
            }}
          />
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer>
          <Button
            content="Cancel"
            type="button"
            disabled={loaders.isSubmitting}
            onClick={onClose}
          />
          <Button
            positive
            content="Save"
            type="submit"
            form="meetingForm"
            loading={loaders.isSubmitting}
            disabled={loaders.isSubmitting}
          />
        </Modal.Footer>
      </Modal.Footer>
    </Modal>
  );
}

MeetingFormModal.propTypes = {
  meeting: PropTypes.shape({
    id: PropTypes.string,
    externalId: PropTypes.string,
    type: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    name: PropTypes.string,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    attendees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }),
  clientExternalId: PropTypes.string,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
};

MeetingFormModal.defaultProps = {
  meeting: {
    id: "",
    externalId: "",
    type: null,
    name: "",
    startDate: null,
    endDate: null,
    attendees: [],
  },
  clientExternalId: "",
  onClose: () => {},
  onDone: () => {},
};

export default MeetingFormModal;
